import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { RetailerNetworkMyStoresV2Page } from './retailer-network-my-stores-v2-page.component';
import { useStoresFilters } from './hooks/use-stores-filters.hook';
import { useStoresLocations } from './hooks/use-stores-locations.hook';
import {
  useGetRetailerNetworkLocationCities,
  useGetRetailerNetworkLocationStates,
} from './hooks/use-stores-filter-options.hook';
import { hasActiveFilterOrSearch } from './retailer-network-my-stores-v2-page.utils';

import { useGetRetailerNetworkLocationGroups } from '../../data-access/queries';

const LOCATION_GROUPS_PAGE_SIZE = 200;

export const RetailerNetworkMyStoresV2PageContainer = () => {
  const { networkId = '' } = useParams<{ networkId: string }>();

  const filters = useStoresFilters();
  const { mapMarkerLocations, mapBoundsLocations } = useStoresLocations({
    networkId,
    stateFilter: filters.stateFilter,
    cityFilter: filters.cityFilter,
    connectionStatusFilter: filters.connectionStatusFilter,
    search: filters.search,
    selectedLocationUuid: filters.selectedLocationUuid,
  });

  const { states: stateOptions } = useGetRetailerNetworkLocationStates(networkId);
  const { cities: fetchedCityOptions, loading: citiesLoading } =
    useGetRetailerNetworkLocationCities(networkId, filters.stateFilter);
  // While cities are (re)loading for an already-selected state (e.g. a shared/bookmarked URL),
  // seed the options with the current selection so its label doesn't flash blank.
  const cityOptions =
    citiesLoading && filters.cityFilter && !fetchedCityOptions.includes(filters.cityFilter)
      ? [filters.cityFilter, ...fetchedCityOptions]
      : fetchedCityOptions;

  const showFlatList = hasActiveFilterOrSearch(filters);

  const {
    data: locationGroups = [],
    loading: isLocationGroupsLoading,
    paginationProps,
  } = useGetRetailerNetworkLocationGroups({
    filter: { retailerNetworkUuidEquals: networkId },
    pageSize: LOCATION_GROUPS_PAGE_SIZE,
    baseOptions: { skip: showFlatList },
  });

  useEffect(() => {
    if (paginationProps?.hasNextPage) {
      console.warn(
        `RetailerNetworkMyStoresV2Page: location groups list is truncated — network ${networkId} has more than ${LOCATION_GROUPS_PAGE_SIZE} groups.`,
      );
    }
  }, [paginationProps?.hasNextPage, networkId]);

  return (
    <RetailerNetworkMyStoresV2Page
      locationGroups={locationGroups}
      isLocationGroupsLoading={isLocationGroupsLoading}
      mapMarkerLocations={mapMarkerLocations}
      mapBoundsLocations={mapBoundsLocations}
      stateOptions={stateOptions}
      cityOptions={cityOptions}
      {...filters}
    />
  );
};
